import React, { memo, useState, useEffect, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { FaTriangleExclamation, FaXmark, FaExpand } from 'react-icons/fa6';
import { ViolinPlotResponse } from './types';
import { categorizeViolinPlots } from './utils/violinPlotImage';

interface ViolinPlotGridProps {
  violinPlots: ViolinPlotResponse;
  imageErrors: Set<string>;
  onImageError: (plotName: string) => void;
}

/**
 * ViolinPlotGrid sub-component
 * Renders the grid of violin plot images with fullscreen support
 * Now uses explicit type information from backend instead of guessing by name
 */
export const ViolinPlotGrid: React.FC<ViolinPlotGridProps> = memo(({
  violinPlots,
  imageErrors,
  onImageError
}) => {
  const { t } = useTranslation('training');
  const titleId = useId();
  const [fullscreenImage, setFullscreenImage] = useState<{ src: string; title: string } | null>(null);

  const openFullscreen = (src: string, title: string) => {
    setFullscreenImage({ src, title });
    document.body.style.overflow = 'hidden';
  };

  const closeFullscreen = () => {
    setFullscreenImage(null);
    document.body.style.overflow = '';
  };

  // Escape closes the fullscreen lightbox.
  useEffect(() => {
    if (!fullscreenImage) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeFullscreen();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [fullscreenImage]);

  // Categorize plots into TIME, Input, and Output using explicit type from backend
  // (shared with the combined-image download so both always match)
  const { inputPlots, timePlots, outputPlots } = categorizeViolinPlots(violinPlots);

  // Helper function to render a single plot card
  const renderPlotCard = (plotName: string, imageSrc: string, label?: string, className?: string) => {
    const displayName = plotName.replace(/^(input|time|output)_/, '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    const hasError = imageErrors.has(plotName);

    return (
      <div key={plotName} className={`rounded-xl border border-border bg-card overflow-hidden transition-shadow duration-150 hover:shadow-md ${className || ''}`}>
        <div className="relative group">
          {hasError ? (
            <div className="flex flex-col items-center justify-center py-10 sm:py-14 px-4">
              <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-amber-50 mb-3">
                <FaTriangleExclamation className="text-amber-500 text-sm" />
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground text-center">
                {t('training.visualization.imageLoadError')}
              </p>
            </div>
          ) : (
            <>
              <img
                src={imageSrc}
                alt={`Violin plot: ${displayName}`}
                className="w-full h-auto cursor-pointer transition-opacity duration-150 hover:opacity-95"
                style={{ maxHeight: '400px', objectFit: 'contain' }}
                onError={() => onImageError(plotName)}
                onClick={() => openFullscreen(imageSrc, displayName)}
              />
              <button
                type="button"
                onClick={() => openFullscreen(imageSrc, displayName)}
                className="absolute top-2.5 right-2.5 w-7 h-7 inline-flex items-center justify-center rounded-lg bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-150 hover:bg-black/70"
                title={t('common.fullscreen') || 'Vollbild'}
                aria-label={t('common.fullscreen') || 'Vollbild'}
              >
                <FaExpand className="text-[10px]" />
              </button>
            </>
          )}
        </div>
        {label && (
          <div className="px-3 py-2 sm:px-4 sm:py-2.5 border-t border-border bg-muted/50">
            <p className="text-center text-xs sm:text-sm font-medium text-foreground tracking-tight">
              {label}
            </p>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="space-y-8">
        {/* Input Plots Section - FIRST */}
        {inputPlots.length > 0 && (
          <div>
            <p className="text-[10px] sm:text-xs uppercase tracking-wide font-medium text-muted-foreground text-center mb-3 sm:mb-4">
              {t('training.visualization.inputDistribution')}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              {inputPlots.map((plot) => renderPlotCard(plot.name, plot.src, plot.label, 'max-w-md'))}
            </div>
          </div>
        )}

        {/* Time Information Section - SECOND */}
        {timePlots.length > 0 && (
          <div>
            <p className="text-[10px] sm:text-xs uppercase tracking-wide font-medium text-muted-foreground text-center mb-3 sm:mb-4">
              {t('training.visualization.timeComponents')}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              {timePlots.map((plot) => renderPlotCard(plot.name, plot.src, plot.label, 'max-w-md'))}
            </div>
          </div>
        )}

        {/* Output Plots Section - THIRD */}
        {outputPlots.length > 0 && (
          <div>
            <p className="text-[10px] sm:text-xs uppercase tracking-wide font-medium text-muted-foreground text-center mb-3 sm:mb-4">
              {t('training.visualization.outputDistribution')}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              {outputPlots.map((plot) => renderPlotCard(plot.name, plot.src, plot.label, 'max-w-md'))}
            </div>
          </div>
        )}
      </div>

      {/* Fullscreen Modal */}
      {fullscreenImage && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm"
          onClick={closeFullscreen}
        >
          <button
            type="button"
            onClick={closeFullscreen}
            className="absolute top-4 right-4 z-10 w-8 h-8 inline-flex items-center justify-center rounded-lg bg-white/10 text-white hover:bg-white/20 active:scale-[0.98] transition-all duration-150"
            title={t('common.close') || 'Schliessen'}
            aria-label={t('common.close') || 'Schliessen'}
          >
            <FaXmark className="text-sm" />
          </button>
          <div className="text-center max-w-full max-h-full" onClick={(e) => e.stopPropagation()}>
            <h3 id={titleId} className="text-white text-sm sm:text-base font-medium mb-3 sm:mb-4 tracking-tight">
              {fullscreenImage.title}
            </h3>
            <img
              src={fullscreenImage.src}
              alt={fullscreenImage.title}
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
            />
          </div>
        </div>
      )}
    </>
  );
});

ViolinPlotGrid.displayName = 'ViolinPlotGrid';

export default ViolinPlotGrid;
